import { createColumnHelper } from '@tanstack/react-table';
import { PropsWithChildren } from 'react';
import {
    PortfolioCentralRateGroupOverview,
    PortfolioCentralSettlementRule,
    PortfolioRateGroupOverview,
    PortfolioCancellationPolicy,
    PortfolioCentralCancellationPolicy,
    PortfolioSettlementRule,
    PortfolioPolicyOverrideStatus,
} from '@mews-commander/data-access';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { ColumnFilterType, Icon, IconName, Link, Body, Stack } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { Dash } from '@mews-commander/ui';
import { compactArray } from '@mews/fp';
import { isCurrentEnterprisePaymentAndCancellationPoliciesExceptionsEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { useCommanderSelector } from '@mews-commander/utils';
import { CancellationPolicyName, SettlementRuleName } from '../../../../../components';
import { PolicyExceptionStatusCell } from '../../../../rateGroups/components/table/cells';
import { RateGroupTabValues } from './types';
import { RowActions } from './RowActions';
import { useRateGroupSidePanelContext } from './sidePanelTabs/RateGroupSidePanelContext';
import { CentralRateGroupRowActions } from './CentralRateGroupRowActions';

const MappedSettlementRules = ({
    settlementRules,
}: {
    settlementRules: (PortfolioSettlementRule | PortfolioCentralSettlementRule)[];
}) => (
    <Stack vertical>
        {settlementRules.map((settlementRule) => (
            <Body size="small" wordBreak="break-all" ellipsis maxLines={1} key={settlementRule.id} as="span">
                <SettlementRuleName {...settlementRule} format="nameAndParameters" />
            </Body>
        ))}
    </Stack>
);

const MappedCancellationPolicies = ({
    cancellationPolicies,
}: {
    cancellationPolicies: (PortfolioCentralCancellationPolicy | PortfolioCancellationPolicy)[];
}) => (
    <Stack vertical>
        {cancellationPolicies && cancellationPolicies.length > 0 ? (
            cancellationPolicies.map((cancellationPolicy) => (
                <Body size="small" wordBreak="break-all" ellipsis maxLines={1} key={cancellationPolicy.id} as="span">
                    <CancellationPolicyName
                        {...cancellationPolicy}
                        applicabilityType={cancellationPolicy.applicability}
                        format="nameAndParameters"
                    />
                </Body>
            ))
        ) : (
            <Dash />
        )}
    </Stack>
);

const LinkCell = ({
    children,
    tab,
    rateGroup,
}: PropsWithChildren<{ rateGroup: PortfolioCentralRateGroupOverview; tab: RateGroupTabValues }>) => {
    const { open } = useRateGroupSidePanelContext();
    return (
        <Link
            standalone
            variant="basic"
            onClick={() => {
                open({ rateGroup, tab });
            }}
            data-disable-click-away
        >
            {children}
        </Link>
    );
};

const centralRateGroupColumnHelper = createColumnHelper<PortfolioCentralRateGroupOverview>();
const rateGroupColumnHelper = createColumnHelper<PortfolioRateGroupOverview>();

export const centralRateGroupColumns = [
    centralRateGroupColumnHelper.accessor('name', {
        header: () => <LocalizedText textKey={TextKey.RateGroupName} />,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
        cell: (props) => (
            <LinkCell rateGroup={props.row.original} tab="details">
                <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                    {props.getValue()}
                </Body>
            </LinkCell>
        ),
    }),
    centralRateGroupColumnHelper.accessor('serviceKey', {
        header: () => <LocalizedText textKey={TextKey.ServiceKey} />,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    centralRateGroupColumnHelper.accessor('properties', {
        header: () => <LocalizedText textKey={TextKey.Properties} />,
        cell: (props) => (
            <LinkCell rateGroup={props.row.original} tab="properties">
                {props.getValue()}
            </LinkCell>
        ),
    }),
    // TODO: Uncomment when assigning property groups to central rate groups is available
    // centralRateGroupColumnHelper.accessor('propertyGroups', {
    //     header: () => <LocalizedText textKey={TextKey.PropertyGroupsNavigation} />,
    //     enableSorting: true,
    //     enableColumnFilter: true,
    //     meta: {
    //         filtersType: ColumnFilterType.String,
    //     },
    //     cell: (props) => {
    //         const cellContent = props.getValue()?.join(', ') || UnicodeEntity.Dash;
    //         return (
    //             <Body size="small" ellipsis maxLines={1}>
    //                 {cellContent}
    //             </Body>
    //         );
    //     },
    // }),
    centralRateGroupColumnHelper.accessor('rates', {
        header: () => <LocalizedText textKey={TextKey.RatesPlural} />,
    }),
    centralRateGroupColumnHelper.accessor('centralSettlementRules', {
        header: () => <LocalizedText textKey={TextKey.PaymentPolicyPlural} />,
        cell: (props) => (
            <LinkCell rateGroup={props.row.original} tab="policies">
                <MappedSettlementRules settlementRules={props.getValue()} />
            </LinkCell>
        ),
    }),
    centralRateGroupColumnHelper.accessor('centralCancellationPolicies', {
        header: () => <LocalizedText textKey={TextKey.CancellationPolicyPlural} />,
        cell: (props) => (
            <LinkCell rateGroup={props.row.original} tab="policies">
                <MappedCancellationPolicies cancellationPolicies={props.getValue()} />
            </LinkCell>
        ),
    }),
    centralRateGroupColumnHelper.accessor('rateGroupKey', {
        header: () => <LocalizedText textKey={TextKey.RateGroupKey} />,
        cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
            filtersType: ColumnFilterType.String,
        },
    }),
    centralRateGroupColumnHelper.display({
        id: 'actions',
        header: '',
        meta: { align: 'end' },
        cell: (props) => <CentralRateGroupRowActions rateGroup={props.row.original} />,
    }),
];

export const useGetRateGroupColumns = () => {
    const { open } = useRateGroupSidePanelContext();
    const arePolicyExceptionsEnabled = useCommanderSelector(
        isCurrentEnterprisePaymentAndCancellationPoliciesExceptionsEnabled
    );
    return compactArray([
        rateGroupColumnHelper.accessor('name', {
            header: () => <LocalizedText textKey={TextKey.RateGroupName} />,
            cell: (props) => (
                <Link
                    data-disable-click-away
                    standalone
                    variant="basic"
                    onClick={() => open({ tab: 'details', rateGroup: props.row.original })}
                >
                    <Stack spacing="100" itemAlignment="center">
                        <Body size="small" wordBreak="break-all" ellipsis maxLines={1} as="span">
                            {props.getValue()}
                        </Body>
                        {props.row.original.isPortfolioManaged ? (
                            <Icon size="small" value={IconName.CustomLink} />
                        ) : null}
                    </Stack>
                </Link>
            ),
        }),
        rateGroupColumnHelper.accessor('property', {
            header: () => <LocalizedText textKey={TextKey.Property} />,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
            cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        }),
        rateGroupColumnHelper.accessor('serviceKey', {
            header: () => <LocalizedText textKey={TextKey.ServiceKey} />,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
        }),
        rateGroupColumnHelper.accessor('rates', {
            header: () => <LocalizedText textKey={TextKey.RatesPlural} />,
            cell: (props) => (
                <Link standalone variant="basic" onClick={() => open({ tab: 'rates', rateGroup: props.row.original })}>
                    {props.getValue()}
                </Link>
            ),
        }),
        rateGroupColumnHelper.accessor('settlementRules', {
            header: () => <LocalizedText textKey={TextKey.PaymentPolicyPlural} />,
            cell: (props) => (
                <Link
                    standalone
                    variant="basic"
                    onClick={() =>
                        open({
                            tab: 'policies',
                            rateGroup: props.row.original,
                        })
                    }
                >
                    <MappedSettlementRules settlementRules={props.getValue()} />
                </Link>
            ),
        }),
        rateGroupColumnHelper.accessor('cancellationPolicies', {
            header: () => <LocalizedText textKey={TextKey.CancellationPolicyPlural} />,
            cell: (props) => (
                <Link
                    standalone
                    variant="basic"
                    onClick={() =>
                        open({
                            tab: 'policies',
                            rateGroup: props.row.original,
                        })
                    }
                >
                    <MappedCancellationPolicies cancellationPolicies={props.getValue()} />
                </Link>
            ),
        }),
        arePolicyExceptionsEnabled
            ? rateGroupColumnHelper.accessor('policyOverrideStatus', {
                  header: () => <LocalizedText textKey={TextKey.GeneralPolicyExceptionPlural} />,
                  cell: (props) => {
                      const policyExceptionStatus = props.getValue();
                      return policyExceptionStatus !== PortfolioPolicyOverrideStatus.Empty ? (
                          <PolicyExceptionStatusCell status={policyExceptionStatus} />
                      ) : (
                          <Dash />
                      );
                  },
              })
            : null,
        rateGroupColumnHelper.accessor('rateGroupKey', {
            header: () => <LocalizedText textKey={TextKey.RateGroupKey} />,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                filtersType: ColumnFilterType.String,
            },
            cell: (props) => props.getValue() ?? UnicodeEntity.Dash,
        }),
        rateGroupColumnHelper.display({
            id: 'actions',
            header: '',
            meta: { align: 'end' },
            cell: (props) => (
                <RowActions
                    onCellClick={open}
                    setRowSelection={props.table.setRowSelection}
                    rateGroup={props.row.original}
                />
            ),
        }),
    ]);
};
